import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useImperativeHandle, useRef, useEffect, useState, } from 'react';
import classNames from 'classnames';
import './MultiSelect.scss';
import { isTouchDevice } from '../../utils';
import { useControllableProp, useDropdown, useKeyControlledList } from '../../hooks';
import { TertiaryBtn, SIZE } from '../btn/TertiaryBtn';
import { SelectDropdown } from './SelectDropdown';
import { Popup, SvgCaretDown, SvgCross } from '../../index';
export const MultiSelect = forwardRef(
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
({ name, defaultValues, values, items, buttons, isHovered, isDisabled, isInvalid, isLoading, inputPlaceholder, inputMinCharCount, minCharCountMessage, noItemsMessage, moreItemsMessage, renderValue, renderItem, compareItems, onInputChange, onChange, testId, }, ref) => {
    const popupTriggerRef = useRef(null);
    const popupContentRef = useRef(null);
    const pillsContainerRef = useRef(null);
    const inputRef = useRef(null);
    const [inputValue, setInputValue] = useState('');
    const [currentValues, setCurrentValues] = useControllableProp({
        value: values,
        defaultValue: defaultValues !== undefined ? defaultValues : [],
    });
    const [keyControlledListItemIndex, handleListControllerKeyDown, resetKeyControlledList] = useKeyControlledList(items.length);
    const [isDropdownShown, setDropdownShown, handleDropdownKeyDown] = useDropdown(popupTriggerRef, popupContentRef, resetKeyControlledList);
    const popupPosition = [
        'bottom-right',
        'bottom-center',
        'bottom-left',
        'top-right',
        'top-center',
        'top-left',
    ];
    useImperativeHandle(ref, () => ({
        focus: () => {
            if (!isTouchDevice()) {
                pillsContainerRef.current && pillsContainerRef.current.focus();
            }
        },
    }));
    function updateValue(e, newValues) {
        setCurrentValues?.(newValues);
        onChange(e, { values: newValues, name: name });
    }
    function handleInputKeyDown(e) {
        handleListControllerKeyDown(e);
        switch (e.keyCode) {
            case 13: // Enter
                if (isDropdownShown && keyControlledListItemIndex !== undefined) {
                    e.preventDefault();
                    handleItemClick(e, {
                        data: { item: items[keyControlledListItemIndex], hideDropdown: true },
                    });
                    pillsContainerRef.current?.focus();
                }
                break;
            case 27: // Escape
                pillsContainerRef.current?.focus();
                break;
        }
    }
    function handleBtnKeyDown(e) {
        if (!isDisabled) {
            handleDropdownKeyDown(e);
        }
    }
    function handleBtnClick() {
        if (!isDisabled) {
            setDropdownShown(!isDropdownShown);
        }
    }
    function toggleItemInArray(array, item) {
        const set = new Set(array);
        if (set.has(item)) {
            set.delete(item);
        }
        else {
            set.add(item);
        }
        return [...set];
    }
    function handleItemClick(e, eventData) {
        const newValues = toggleItemInArray(currentValues, eventData.data.item);
        updateValue(e, newValues);
        if (eventData.data.hideDropdown) {
            setDropdownShown(false);
        }
        else {
            e.stopPropagation();
        }
        if (!isTouchDevice()) {
            pillsContainerRef.current?.focus();
        }
    }
    function handleInputChange(e, eventData) {
        resetKeyControlledList();
        setInputValue(eventData.value);
        onInputChange(e, eventData);
    }
    function handlePillClick(e, eventData) {
        e.stopPropagation();
        const newValues = currentValues.filter((value) => value !== eventData.data);
        updateValue(e, newValues);
        setDropdownShown(false);
    }
    function fakeClick() {
        // TertiaryBtn is used for visual consistency
    }
    useEffect(() => {
        inputRef.current?.focus();
    }, [isDropdownShown]);
    return (_jsxs("div", { className: 'sg-multiSelect', ref: popupTriggerRef, children: [_jsxs("div", { className: 'sg-multiSelect--pillsWrapper', children: [_jsx("ul", { className: classNames({
                            'sg-multiSelect--pillList': true,
                            [`sg-multiSelect--pillList-btnsCount-${(buttons?.length || 0) + 1}`]: true,
                        }), ref: pillsContainerRef, tabIndex: isDisabled ? undefined : 0, onKeyDown: handleBtnKeyDown, onClick: handleBtnClick, "aria-disabled": isDisabled, "aria-invalid": isInvalid, "data-hovered": isHovered, "data-test-id": testId, children: currentValues?.map((value, index) => (_jsxs("li", { className: 'sg-multiSelect--pill', children: [_jsx("span", { className: 'sg-multiSelect--pillLabel', title: renderValue(value), children: renderValue(value) }), !isDisabled && (_jsx("span", { className: 'sg-multiSelect--pillRemoveBtn', children: _jsx(TertiaryBtn, { icon: { element: _jsx(SvgCross, {}) }, size: SIZE.SML, data: value, isDanger: true, onClick: handlePillClick, testId: testId && `${testId}-pill-${index}` }) }))] }, `${renderValue(value)}-${index}`))) }), _jsxs("div", { className: 'sg-multiSelect--actions', children: [_jsx("div", { className: 'sg-multiSelect--arrow', children: _jsx(TertiaryBtn, { icon: { element: _jsx(SvgCaretDown, {}) }, tabIndex: -1, onClick: fakeClick }) }), buttons?.map((btn, index) => {
                                return (_jsx(TertiaryBtn, { icon: { element: btn.icon }, tooltip: btn.tooltip, isDanger: btn.isDanger, tabIndex: -1, data: name, onClick: btn.onClick }, index));
                            })] })] }), isDropdownShown && (_jsx(Popup, { position: popupPosition, triggerRef: popupTriggerRef, sameWidthAsTrigger: true, children: _jsx("div", { ref: popupContentRef, "data-test-id": testId && `${testId}-dropdown`, children: _jsx(SelectDropdown, { inputRef: inputRef, name: name, inputValue: inputValue, inputPlaceholder: inputPlaceholder, onInputKeyDown: handleInputKeyDown, onInputChange: handleInputChange, activeItemIndex: keyControlledListItemIndex, items: items, selectedItems: currentValues, isLoading: !!isLoading, inputMinCharCount: inputMinCharCount, minCharCountMessage: minCharCountMessage, noItemsMessage: noItemsMessage, moreItemsMessage: moreItemsMessage, renderItem: renderItem, compareItems: compareItems, onItemClick: handleItemClick, testId: testId }) }) }))] }));
});
//# sourceMappingURL=MultiSelect.js.map